import React, { useState, useEffect } from 'react';
import { X, Search, FileText, Loader2, Link } from 'lucide-react';
import templateApi from '../api/templateApi';

const TemplatePickerModal = ({ workspaceId, onClose, onSelect }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    useEffect(() => {
        const fetchTemplates = async () => {
            console.log("[TemplatePicker] Fetching templates for workspace:", workspaceId);
            try {
                // Backend returns { data: [], pagination: {} } — extract the data array
                const response = await templateApi.getTemplates(workspaceId, true);
                
                console.log("[TemplatePicker] API Response Raw:", response);
                
                const list = Array.isArray(response) ? response : (response?.data || []);
                
                // Filter to only active templates on the frontend side for reliability
                const activeOnes = list.filter(t => t.status === 'ACTIVE' || t.isActive);
                console.log("[TemplatePicker] Found active templates:", activeOnes.length);
                
                setTemplates(activeOnes);
            } catch (err) {
                console.error("[TemplatePicker] Failed to fetch templates:", err);
            } finally {
                setLoading(false);
            }
        };

        if (workspaceId) {
            fetchTemplates();
        } else {
            console.warn("[TemplatePicker] No workspaceId provided to modal. Falling back to activeWorkspace from localStorage if possible.");
            const activeW = localStorage.getItem('activeWorkspace');
            if (activeW) {
                try {
                    const wObj = JSON.parse(activeW);
                    if (wObj && wObj._id) {
                        console.log("[TemplatePicker] Using fallback workspace ID:", wObj._id);
                        // We could re-call fetchTemplates here but let's just log for now 
                        // as workspaceId prop should ideally be present.
                    }
                } catch(e) {}
            }
        }
    }, [workspaceId]);

    const handleSelectTemplate = async (template) => {
        setSelectedTemplate(template);
        setPreviewLoading(true);
        try {
            const data = await templateApi.previewTemplate(workspaceId, {
                subject: template.subject,
                bodyHtml: template.bodyHtml
            });
            setPreviewData(data);
        } catch (err) {
            console.error("Failed to generate preview:", err);
            // Fallback preview
            setPreviewData({
                resolvedSubject: template.subject,
                resolvedBodyHtml: template.bodyHtml,
                variablesFound: []
            });
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleApply = () => {
        if (selectedTemplate) {
            onSelect({
                _id: selectedTemplate._id,
                name: selectedTemplate.name,
                subject: selectedTemplate.subject,
                bodyHtml: selectedTemplate.bodyHtml
            });
        }
    };

    const filteredTemplates = templates.filter(t => 
        t.name.toLowerCase().includes(search.toLowerCase()) || 
        t.subject.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[110] flex items-center justify-center p-4">
            <div className="bg-card text-card-foreground border-2 border-border/50 w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="p-5 border-b flex items-center justify-between bg-muted/30">
                    <div>
                        <h3 className="text-xl font-bold tracking-tight">Select Template</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Choose an existing template to apply to this node.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-muted rounded-full">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden">
                    {/* Left: Template List */}
                    <div className="w-1/2 border-r flex flex-col bg-background">
                        <div className="p-4 border-b">
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search templates..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full bg-muted/50 border rounded-lg pl-9 pr-4 py-2 text-sm focus:border-primary outline-none"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-2 space-y-1">
                            {loading ? (
                                <div className="py-12 flex justify-center">
                                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : filteredTemplates.length === 0 ? (
                                <div className="py-8 text-center text-sm text-muted-foreground">
                                    No templates found.
                                </div>
                            ) : (
                                filteredTemplates.map(t => (
                                    <button
                                        key={t._id}
                                        onClick={() => handleSelectTemplate(t)}
                                        className={`w-full text-left p-3 rounded-lg flex items-start gap-3 transition-colors ${
                                            selectedTemplate?._id === t._id 
                                                ? 'bg-primary/10 border-primary/30 border' 
                                                : 'hover:bg-muted border border-transparent'
                                        }`}
                                    >
                                        <div className={`p-2 rounded-md ${selectedTemplate?._id === t._id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                                            <FileText className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className={`font-semibold text-sm truncate ${selectedTemplate?._id === t._id ? 'text-primary' : ''}`}>
                                                {t.name}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                                                {t.subject}
                                            </div>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right: Preview */}
                    <div className="w-1/2 flex flex-col bg-muted/10">
                        {selectedTemplate ? (
                            <div className="flex flex-col h-full">
                                <div className="p-4 border-b bg-background font-semibold text-sm flex items-center gap-2">
                                    <Link className="w-4 h-4 text-muted-foreground" />
                                    Preview: {selectedTemplate.name}
                                </div>
                                <div className="flex-1 overflow-y-auto p-6">
                                    {previewLoading ? (
                                        <div className="flex justify-center py-12">
                                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                                        </div>
                                    ) : previewData ? (
                                        <div className="space-y-4">
                                            <div className="bg-background border rounded-lg overflow-hidden shadow-sm">
                                                <div className="p-3 bg-muted/40 border-b flex flex-col gap-1 text-sm">
                                                    <div className="flex gap-2">
                                                        <span className="text-muted-foreground w-12 text-right text-xs uppercase font-bold tracking-wider mt-0.5">Subj:</span>
                                                        <span className="font-semibold">{previewData.resolvedSubject || <i className="text-muted-foreground">No subject</i>}</span>
                                                    </div>
                                                </div>
                                                <div className="p-4 min-h-[250px] text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: previewData.resolvedBodyHtml || '<i class="text-muted-foreground">No body content</i>' }} />
                                            </div>
                                            
                                            {previewData.variablesFound?.length > 0 && (
                                                <div className="text-xs text-muted-foreground flex gap-2 items-center flex-wrap mt-4">
                                                    <span className="font-semibold uppercase tracking-wider">Detected Variables:</span>
                                                    {previewData.variablesFound.map(v => (
                                                        <span key={v} className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-mono text-[10px]">
                                                            {v}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                                <div className="p-4 border-t bg-background flex justify-end gap-3">
                                    <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={handleApply}
                                        className="px-6 py-2 bg-primary text-primary-foreground text-sm font-bold rounded-lg shadow-md hover:bg-primary/90 flex items-center gap-2"
                                    >
                                        Use Template
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-8 text-center">
                                <FileText className="w-12 h-12 mb-4 opacity-20" />
                                <p className="text-sm font-medium">Select a template from the list to preview.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemplatePickerModal;
